import { useLocation } from '@remix-run/react'
import type { Dispatch, SetStateAction } from 'react'
import { createContext, use, useEffect, useRef, useState } from 'react'

import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useCurrentPage } from '~/hooks/useCurrentPage'
import { useTailwindBreakpoint } from '~/hooks/useTailwindBreakpoint'

export type NavbarContextType = {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

export const NavbarContext = createContext<NavbarContextType | null>(null)

export const useNavbarContext = () => {
  const context = use(NavbarContext)

  if (!context) {
    throw new Error(
      'useNavbarContext must be used within a NavbarContextProvider'
    )
  }

  return context
}

interface NavbarContextProviderProps {
  children: React.ReactNode
}

export const NavbarContextProvider = ({
  children,
}: NavbarContextProviderProps) => {
  const page = useCurrentPage()
  const { pathname } = useLocation()
  const previousPathname = useRef(pathname)
  const seller = useOptionalSeller()

  const isMd = useTailwindBreakpoint('md')

  // Only auto-open sidebar when seller lands on the homepage
  // (not on settings/orders pages to avoid sidebar opening on redirects like domain change)
  const [isOpen, setIsOpen] = useState(!!seller && page === 'home')

  useEffect(() => {
    // on mobile collapse the navbar when navigating to a new page
    if (!isMd && previousPathname.current !== pathname) {
      setIsOpen(false)
    }

    previousPathname.current = pathname
  }, [isMd, pathname])

  return <NavbarContext value={{ isOpen, setIsOpen }}>{children}</NavbarContext>
}
